import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { fetchDiscountObject } from '../Cart/Cart.utils';
import { DISCOUNT_ALLOCATIONS, SITE_CONFIG, CURRENT_USER } from '../App.gql';
import { isPreview } from '../../graphql/previews';

import * as Styled from './DiscountAppliedMessage.styled';

/**
 * If a discount code has been automatically applied to a customer's cart, show messaging for how much they have auto applied
 */
const DiscountAppliedMessage = ({ withIcon, heading, className }) => {
  const { data: userData } = useQuery(CURRENT_USER);
  const cartId = userData?.cartId;

  const { data: couponData } = useQuery(SITE_CONFIG, {
    variables: { key: 'coupon', preview: isPreview('settings') }
  });
  const { data: configData } = useQuery(SITE_CONFIG, {
    variables: { key: 'site', preview: isPreview('settings') }
  });

  const {
    loading,
    error,
    data: discountData
  } = useQuery(DISCOUNT_ALLOCATIONS, {
    context: { service: 'storefront' },
    variables: { cartId },
    skip: !cartId
  });

  if (loading || error) return null;

  const coupons = couponData?.settings[0]?.values;
  const siteConfig = configData?.settings[0]?.values;
  const codeAllocation = fetchDiscountObject(discountData?.cart);

  if (!codeAllocation) return null;

  const { code } = codeAllocation;
  const codeDiscountPercentage = siteConfig?.coupon_code_percentage?.value;
  const message = coupons?.[code]?.value || undefined;

  return (
    <div className={className}>
      {heading && <Styled.Heading>{heading}</Styled.Heading>}
      <Styled.DiscountAppliedMessage $radius={withIcon ? 'large' : 'small'}>
        {withIcon && <Styled.Glyph>y</Styled.Glyph>}
        <Styled.Container align={withIcon ? 'left' : 'center'}>
          <Styled.Title>
            Additional {codeDiscountPercentage}% Off Auto-Applied in Bag
          </Styled.Title>
          {!!message && <Styled.Subtitle>{message}</Styled.Subtitle>}
        </Styled.Container>
      </Styled.DiscountAppliedMessage>
    </div>
  );
};

DiscountAppliedMessage.propTypes = {
  /**
   * Displays a glyph with additional styling
   */
  withIcon: PropTypes.bool,
  /**
   * A title for the message
   */
  heading: PropTypes.string,
  /**
   * passed via styled components
   */
  className: PropTypes.string
};

DiscountAppliedMessage.defaultProps = {
  withIcon: false,
  heading: undefined,
  className: undefined
};

export default DiscountAppliedMessage;
